// Source detection for the Files-tab import form, mirroring the backend's
// detectSourceType (VideoImportService): Google Drive/Docs hosts -> gdrive,
// YouTube hosts -> youtube, any other http(s) URL -> direct.
export const driveUrlPattern =
	/^(https?:\/\/)?([a-z0-9-]+\.)*(drive|docs)\.google\.com([/?#]|$)/i;
export const oneDriveUrlPattern =
	/^(https?:\/\/)?([a-z0-9-]+\.)*(1drv\.ms|onedrive\.live\.com|sharepoint\.com)([/?#]|$)/i;
export const youtubeUrlPattern =
	/^(https?:\/\/)?([a-z0-9-]+\.)*(youtube\.com|youtu\.be)([/?#]|$)/i;
// Anything else must at least look like an http(s) URL with a host.
export const directUrlPattern = /^https?:\/\/[^\s/$.?#]+\.[^\s]*$/i;
// Drive FOLDER links (mirrors the backend's parseFolderId) — expand into one
// import per top-level video file via the create-folder endpoint.
export const driveFolderPattern =
	/drive\.google\.com\/(?:drive\/)?(?:u\/\d+\/)?folders\/([\w-]+)/i;

export const isDriveFolderUrl = (url: string) =>
	driveFolderPattern.test(url.trim());

export type VideoImportSource = 'gdrive' | 'onedrive' | 'youtube' | 'direct';

export const detectSource = (url: string): VideoImportSource | null => {
	const value = url.trim();
	if (!value) return null;
	if (driveUrlPattern.test(value)) return 'gdrive';
	if (oneDriveUrlPattern.test(value)) return 'onedrive';
	if (youtubeUrlPattern.test(value)) return 'youtube';
	if (directUrlPattern.test(value)) return 'direct';
	return null;
};

// Submittable = a recognized source pasted as a real http(s) URL (the
// backend's url rule rejects scheme-less values).
export const isImportableUrl = (url: string) =>
	/^https?:\/\//i.test(url.trim()) && detectSource(url) !== null;

// Bulk input: one video per line, "url | optional name". The name separator
// is the FIRST pipe — line-based parsing on purpose (URLs can legally contain
// commas, and parallel comma lists mispair names silently).
export interface ImportLineEntry {
	/** 1-based line number in the textarea, for error messages. */
	line: number;
	url: string;
	title?: string;
	source: VideoImportSource | null;
	/** 'folder' = Drive folder link (expands server-side, one row per video). */
	kind: 'file' | 'folder';
	valid: boolean;
}

export const parseImportLines = (raw: string): ImportLineEntry[] =>
	raw
		.split('\n')
		.map((text, index) => ({ text: text.trim(), line: index + 1 }))
		.filter(({ text }) => text.length > 0)
		.map(({ text, line }) => {
			const pipeIndex = text.indexOf('|');
			const url = (pipeIndex === -1 ? text : text.slice(0, pipeIndex)).trim();
			const title =
				pipeIndex === -1 ? undefined : text.slice(pipeIndex + 1).trim() || undefined;
			const kind = isDriveFolderUrl(url) ? ('folder' as const) : ('file' as const);
			return {
				line,
				url,
				title,
				source: detectSource(url),
				kind,
				valid: isImportableUrl(url),
			};
		});
